<template>

    <div class="min-h-screen bg-[rgb(13,13,13)] text-white">
        <HeaderV2
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :routePrefix="routePrefix"
            :requiresAgeVerification="requiresAgeVerification"
            @goldJoinClick="isBuyStarsModalOpen = true"
            @joinClick="handleLogin"
        />

        <SectionNav />

        <!-- ── Hero ──────────────────────────────────────────────────────── -->
        <section
            id="top"
            class="relative overflow-hidden w-full"
            style="background-image: linear-gradient(135deg, rgb(13,13,13) 0%, rgb(20,10,32) 55%, rgb(13,13,13) 100%)"
        >
        <div class="max-w-[1480px] mx-auto flex flex-col lg:flex-row gap-16 lg:gap-[80px] justify-between items-center min-h-screen lg:min-h-[900px] px-5 py-16 lg:pt-4 lg:px-24 w-full text-sm leading-5">
            <!-- Radial glow overlay -->
            <div
                class="pointer-events-none absolute inset-0"
                style="background-image: radial-gradient(at 68% 50%, rgba(139,92,246,0.08) 0%, rgba(0,0,0,0) 60%)"
            />

            <!-- Left content -->
            <div class="z-[2] max-w-[560px] relative w-full">
                <!-- Badge -->
                <div
                    class="inline-flex items-center mb-6 lg:mb-8 text-[10px] font-semibold uppercase tracking-[1.5px] rounded-full px-3 py-1 backdrop-blur-md"
                    style="color: rgb(167,139,250); background-color: rgba(139,92,246,0.05); border: 1px solid rgba(255,255,255,0.08)"
                >
                    ✦ Become a Producer
                </div>

                <!-- Heading -->
                <h1 class="text-[36px] lg:text-[64px] font-bold leading-[1.05] tracking-tight text-white mt-4 mb-6">
                    Pitch the Next<br />
                    <span class="text-amber-400">Naughty America</span><br />
                    Fantasy
                </h1>

                <!-- Subtext -->
                <p class="mb-8 lg:mb-12 text-base lg:text-[19px] leading-[1.6] text-white/70 font-medium max-w-[480px]">
                    You create it. The community backs it. We film it. Your vision
                    becomes the next premium scene watched by millions.
                </p>

                <!-- Stats -->
                <div class="flex gap-6 lg:gap-12 mb-10 lg:mb-14">
                    <div>
                        <div class="text-xl lg:text-3xl font-semibold tracking-tight">14K+</div>
                        <div class="mt-1.5 text-[11px] font-medium uppercase tracking-[1px] text-white/60">Videos Produced</div>
                    </div>
                    <div>
                        <div class="text-xl lg:text-3xl font-semibold tracking-tight">500+</div>
                        <div class="mt-1.5 text-[11px] font-medium uppercase tracking-[1px] text-white/60">Performers</div>
                    </div>
                    <div>
                        <div class="text-xl lg:text-3xl font-semibold tracking-tight">∞</div>
                        <div class="mt-1.5 text-[11px] font-medium uppercase tracking-[1px] text-white/60">Your Imagination</div>
                    </div>
                </div>

                <!-- CTAs -->
                <div class="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <a
                        href="#pitch-form"
                        @click.prevent="handleStartPitch"
                        class="inline-flex items-center justify-center w-full sm:w-auto gap-2 text-white text-base font-semibold rounded-xl px-8 py-4 transition-all duration-300 no-underline shadow-lg shadow-violet-500/20 hover:shadow-violet-500/30 hover:scale-[1.02]"
                        style="background-image: linear-gradient(135deg, rgb(139,92,246), rgb(124,58,237))"
                    >
                        Start Your Pitch →
                    </a>
                    <a
                        href="#pitch-showcase"
                        @click.prevent="handleBrowsePitches"
                        class="inline-flex items-center justify-center w-full sm:w-auto gap-2 text-white/80 text-base font-semibold rounded-xl px-8 py-4 transition-all duration-300 no-underline bg-white/[0.04] border border-white/[0.1] hover:bg-white/[0.08] hover:text-white hover:border-white/[0.2] hover:scale-[1.02]"
                    >
                        Browse Pitches ↓
                    </a>
                </div>
            </div>

            <!-- Right: performer cards -->
            <div class="z-[2] flex gap-4 sm:gap-8 shrink-0 items-end relative w-full lg:w-auto overflow-x-auto pb-8 lg:pb-0 snap-x">
                <!-- Card 1 (side) -->
                <div class="w-[140px] h-[200px] sm:w-[180px] sm:h-[260px] shrink-0 snap-center relative overflow-hidden rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-xl shadow-2xl">
                    <img
                        v-if="heroPerformers[0]"
                        :src="heroPerformers[0].thumbnail_url"
                        :alt="heroPerformers[0].name"
                        class="w-full h-full object-cover object-top"
                    />
                    <div
                        v-if="heroPerformers[0]"
                        class="absolute bottom-0 left-0 right-0 text-[13px] font-medium px-4 pb-4 pt-12"
                        style="background-image: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)"
                    >{{ heroPerformers[0].name }}</div>
                </div>

                <!-- Card 2 (featured, taller) -->
                <div class="w-[160px] h-[240px] sm:w-[200px] sm:h-[300px] shrink-0 snap-center relative overflow-hidden rounded-2xl bg-white/[0.03] border border-violet-500/30 backdrop-blur-xl shadow-[0_24px_60px_-12px_rgba(139,92,246,0.25)]">
                    <img
                        v-if="heroPerformers[1]"
                        :src="heroPerformers[1].thumbnail_url"
                        :alt="heroPerformers[1].name"
                        class="w-full h-full object-cover object-top"
                    />
                    <div
                        v-if="heroPerformers[1]"
                        class="absolute bottom-0 left-0 right-0 text-[13px] font-medium px-4 pb-4 pt-12"
                        style="background-image: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)"
                    >{{ heroPerformers[1].name }}</div>
                </div>

                <!-- Card 3 (side) -->
                <div class="w-[140px] h-[200px] sm:w-[180px] sm:h-[260px] shrink-0 snap-center relative overflow-hidden rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-xl shadow-2xl">
                    <img
                        v-if="heroPerformers[2]"
                        :src="heroPerformers[2].thumbnail_url"
                        :alt="heroPerformers[2].name"
                        class="w-full h-full object-cover object-top"
                    />
                    <div
                        v-if="heroPerformers[2]"
                        class="absolute bottom-0 left-0 right-0 text-[13px] font-medium px-4 pb-4 pt-12"
                        style="background-image: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)"
                    >{{ heroPerformers[2].name }}</div>
                </div>
            </div>
            </div>
        </section>

        <HowItWorks />

        <PitchShowcase
            :nowCasting="nowCasting"
            :inRace="inRace"
            :trending="trending"
            :fresh="fresh"
            @explore="handleExplorePitches"
            @exploreAuditions="handleExploreAuditions"
        />

        <BuyStarsModal
            :isOpen="isBuyStarsModalOpen"
            :userBalance="userBalance"
            @close="isBuyStarsModalOpen = false"
            :starPacks="props.starPacks"
            @buy="handleBuyStarPack"
        />

        <!-- ── Pitch CTA ─────────────────────────────────────────────────── -->
        <section id="pitch-form" class="relative overflow-hidden py-20 px-5 text-center" style="background-image: linear-gradient(135deg, rgba(109,40,217,0.15) 0%, rgba(139,92,246,0.08) 50%, rgba(109,40,217,0.15) 100%); border-top: 1px solid rgba(139,92,246,0.2); border-bottom: 1px solid rgba(139,92,246,0.2)">
            <!-- Glow orb -->
            <div class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] rounded-full" style="background: radial-gradient(ellipse at center, rgba(139,92,246,0.18) 0%, transparent 70%); filter: blur(40px)" />

            <div class="relative z-10 max-w-lg mx-auto">
                <!-- Stat line -->
                <p v-if="totalPitchCount > 0" class="text-[11px] font-semibold uppercase tracking-[1.5px] mb-6" style="color: rgba(167,139,250,0.8)">
                    {{ totalPitchCount }}+ pitches already in the queue
                </p>

                <h2 class="text-3xl lg:text-4xl font-bold text-white mb-4">Ready to pitch your scene?</h2>
                <p class="text-white/60 text-base mb-8 max-w-md mx-auto">Build your pitch in minutes. Performers audition for it. The community picks the winner.</p>

                <!-- Feature chips -->
                <div class="flex flex-wrap justify-center gap-3 mb-10">
                    <span class="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full px-3 py-1.5" style="background: rgba(139,92,246,0.12); border: 1px solid rgba(139,92,246,0.3); color: rgba(196,181,253,0.9)">
                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="star" class="w-3.5 h-3.5 object-contain" />
                        5 stars to submit
                    </span>
                    <span class="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full px-3 py-1.5" style="background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.25); color: rgba(110,231,183,0.9)">
                        <Users style="width:13px;height:13px" />
                        Community votes
                    </span>
                    <span class="inline-flex items-center gap-1.5 text-[12px] font-medium rounded-full px-3 py-1.5" style="background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.25); color: rgba(253,211,130,0.9)">
                        <Film style="width:13px;height:13px" />
                        NA films the winner
                    </span>
                </div>

                <!-- CTA button -->
                <div class="relative inline-block">
                    <div class="absolute inset-0 rounded-xl blur-xl opacity-50" style="background: linear-gradient(135deg, rgb(139,92,246), rgb(124,58,237))" />
                    <button
                        @click="handleStartPitchBottom"
                        class="relative inline-flex items-center justify-center gap-2 text-white text-base font-semibold rounded-xl px-10 py-4 transition-all duration-300 shadow-lg shadow-violet-500/30 hover:scale-[1.02]"
                        style="background-image: linear-gradient(135deg, rgb(139,92,246), rgb(124,58,237))"
                    >
                        Start Your Pitch →
                    </button>
                </div>
            </div>
        </section>

        <CastingSignupModal :isOpen="isCastingSignupModalOpen"
                            :source="'SFW_Producer_Casting'"
                            :sfwTrigger="requiresAgeVerification"
                            @close="handleCastingSignupModalClose"
        />

    </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { Users, Film } from 'lucide-vue-next'
import HeaderV2       from '@/Components/Reels/HeaderV2.vue'
import SectionNav    from '@/Components/Producer/SectionNav.vue'
import HowItWorks    from '@/Components/Producer/HowItWorks.vue'
import PitchShowcase from '@/Components/Producer/PitchShowcase.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import CastingSignupModal from "@/Components/Casting/CastingSignupModal.vue";
import {trackEvent, trackFathomEvent} from "@/Helpers/analytics";
import {getMainAuditionsUrl} from "@/Helpers/auditions";

const props = defineProps({
    heroPerformers:   { type: Array,  default: () => [] },
    nowCasting:       { type: Array,  default: () => [] },
    inRace:           { type: Array,  default: () => [] },
    trending:         { type: Array,  default: () => [] },
    fresh:            { type: Array,  default: () => [] },
    performers:       { type: Array,  default: () => [] },
    starPacks:       { type: Array,  default: () => [] },
    tags:             { type: Array,  default: () => [] },
})

const handleLogin = () => {
    window.location.href = route('members2.auth.login', { dest: '/producer' })
}

const isBuyStarsModalOpen = ref(false)
const isCastingSignupModalOpen = ref(false)

const page = usePage()

const isUserLoggedIn          = computed(() => !!(page?.props?.auth?.user?.id))
const isGoldUser              = computed(() => !!(page?.props?.auth?.is_gold))
const routePrefix             = computed(() => page?.props?.routePrefix || 'www')
const requiresAgeVerification = computed(() => !!(page?.props?.requiresAgeVerification))
const userBalance          = computed(() => Number(page?.props?.auth?.user?.wallet_balance) || 0)

onMounted(() => {
    trackEvent('Producer Page Viewed')

    if (requiresAgeVerification.value) {
        isCastingSignupModalOpen.value = true
    }
})

const handleCastingSignupModalClose = () => {
    isCastingSignupModalOpen.value = false
}

const handleStartPitch = () => {
    trackFathomEvent('Producer_Hero_Start_Pitch');
    trackEvent('Producer Start Pitch Hero Button Clicked')
    router.visit(route(routePrefix.value + '.studio.create-scene'))
}

const handleStartPitchBottom = () => {
    trackFathomEvent('Producer_Bottom_Start_Pitch');
    trackEvent('Producer Start Pitch Bottom Button Clicked')
    router.visit(route(routePrefix.value + '.studio.create-scene'))
}

const handleBrowsePitches = () => {
    trackFathomEvent('Producer_Hero_Browse_Pitches');
    trackEvent('Producer Browse Pitches Hero Button Clicked')
    scrollTo('pitch-showcase')
}

const handleBuyStarPack = (pack) => {
    // Redirect to purchase URL if available
    trackFathomEvent('Producer_Purchase_Stars_Clicked');
    trackEvent('Producer Purchase Stars Button Clicked')

    if (pack.purchaseUrl) {
        window.location.href = pack.purchaseUrl
    }
    // Modal will close automatically or user can close it
}

const handleExplorePitches = () => {
    trackFathomEvent('Producer_Explore_All_Castings_Click');
    trackEvent('Producer Explore All Castings Button Clicked')

    router.visit(
        route(routePrefix.value + '.reels.tag', { view: 'castings' })
    );
}

const handleExploreAuditions = () => {
    trackFathomEvent('Producer_Explore_All_Auditions_Click');
    trackEvent('Producer Explore All Auditions Button Clicked')

    router.visit(
        getMainAuditionsUrl(isUserLoggedIn.value, routePrefix.value)
    );
}

// Pick 3 spread-out performers for the hero cards (not just index 0,1,2 which are all 'A...')
const heroPerformers = computed(() => {
    if (props.heroPerformers && props.heroPerformers.length > 0)
        return props.heroPerformers
    const list = props.performers
    if (list.length === 0) return []
    const step = Math.max(1, Math.floor(list.length / 4))
    return [
        list[step]             ?? list[0],
        list[step * 2]         ?? list[1],
        list[step * 3]         ?? list[2],
    ]
})

const totalPitchCount = computed(() => {
    const ids = new Set()
    for (const list of [props.nowCasting, props.inRace, props.trending, props.fresh]) {
        for (const item of list) if (item?.id) ids.add(item.id)
    }
    return ids.size
})

function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>
